// @ts-nocheck
import React, { useEffect, useState } from "react";
import DOMPurify from 'dompurify';
import { CloseButton } from "../../../Componentes/feedback/dialog/CloseButton";
import AlertMessage from "../../../Alias/AlertMessage";
import DrafterFirma from "./DrafterFirma";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
    CircularProgress,
    Chip,
    Skeleton,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { useDifusiones } from "../../context/DifusionesContext";
import { useAuth } from "../../../Auth/State/AuthContext";
import { useBody } from "../../../Body/state/BodyContext";

export default function Firma({ onClose }) {
    const { obtenerAliasesRemitente, obtenerFirmaAlias } = useDifusiones();
    const { setMessage } = useBody();
    const [aliases, setAliases] = useState([]);
    const [firmasContent, setFirmasContent] = useState({});
    const [loadingAlias, setLoadingAlias] = useState({});
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const { hasRole } = useAuth();

    useEffect(() => {
        async function fetchAliases() {
            setLoading(true);
            try {
                const data = await obtenerAliasesRemitente();
                setAliases(data);
            } catch (error) {
                console.error("Error al obtener aliases:", error);
                setMessage("Hubo un error al traer los aliases");
            } finally {
                setLoading(false);
            }
        }
        fetchAliases();
    }, []);

    const loadFirmaForAlias = async (nombreAlias) => {
        if (nombreAlias in firmasContent) return;
        setLoadingAlias(prev => ({ ...prev, [nombreAlias]: true }));
        try {
            const response = await obtenerFirmaAlias(nombreAlias);
            const firmaHtml = Array.isArray(response) ? response[0]?.firmaHtml ?? null : null;
            setFirmasContent(prev => ({ ...prev, [nombreAlias]: firmaHtml }));
        } catch (error) {
            console.error("Error al cargar firma:", error);
        } finally {
            setLoadingAlias(prev => ({ ...prev, [nombreAlias]: false }));
        }
    };

    const refreshFirmaForAlias = async (nombreAlias) => {
        setFirmasContent(prev => {
            const next = { ...prev };
            delete next[nombreAlias];
            return next;
        });
        await loadFirmaForAlias(nombreAlias);
    };

    const handleChange = (panel, nombreAlias) => (_, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
        if (isExpanded) loadFirmaForAlias(nombreAlias);
    };

    return (
        <>
            <CloseButton onClose={onClose} />
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    {[0, 1, 2].map((i) => (
                        <Box
                            key={i}
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, py: 1.5 }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Skeleton variant="circular" width={20} height={20} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="35%" height={20} />
                                    <Skeleton variant="text" width="55%" height={16} />
                                </Box>
                                <Skeleton variant="circular" width={24} height={24} />
                            </Box>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    <AlertMessage alert={toast} setAlert={setToast} />

                    {aliases.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 6, color: 'text.secondary' }}>
                            <AlternateEmailIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                            <Typography variant="body1">No posee alias para editar sus firmas.</Typography>
                        </Box>
                    ) : (
                        aliases.map((alias, index) => (
                            <Accordion
                                key={index}
                                expanded={expanded === index}
                                onChange={handleChange(index, alias.nombreAlias)}
                                elevation={0}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '8px !important',
                                    '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <AlternateEmailIcon fontSize="small" color="action" />
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {alias.nombreAlias}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {alias.mail}
                                            </Typography>
                                        </Box>
                                        {firmasContent[alias.nombreAlias] === null && (
                                            <Chip label="Sin firma" size="small" variant="outlined" color="warning" sx={{ ml: 1 }} />
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {loadingAlias[alias.nombreAlias] ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : hasRole(["ROLE_EDITAR_FIRMA"]) ? (
                                        <DrafterFirma
                                            firma={{ ...alias, firmaHtml: firmasContent[alias.nombreAlias] ?? null }}
                                            onClose={() => setExpanded(false)}
                                            fetchFirmas={() => refreshFirmaForAlias(alias.nombreAlias)}
                                        />
                                    ) : (
                                        <Box
                                            sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(firmasContent[alias.nombreAlias] || '<em>Este alias no tiene firma</em>') }}
                                        />
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))
                    )}
                </Box>
            )}
        </>
    );
}
